import { invariant } from '@e-socna/utils';
import { getFragmentData, MissionKindEnum } from '@e-socna/data-layer';
import type { FragmentType, Id } from '@e-socna/data-layer';

import { Wizard } from '../../components/Wizard';
import type { FolderBusinessEntity_FolderBusinessEntityConsolidation_Fragment } from '../../pages/Folder/fragments/folder-business-entity-consolidation.fragment';

import { FormVariant } from '../enums/variant.enum';

import { useMissionSteps } from './hooks/useSteps.hook';
import { MissionForm_Mission_Fragment } from './fragments/mission.fragment';
import { useMissionSubmit } from './hooks/useSubmit.hook';
import { useInitialValuesFactory } from './hooks/useInitialValuesFactory.hook';
import { useSubMissionTurnLists } from './hooks/useSubMissionTurnLists.hook';
import type { MissionMutationResult } from './hooks/useSubmit.hook';
import type { MissionFormTab } from './enums/tab.enum';

interface Props {
  readonly askMemoBeforeSubmit?: boolean;
  readonly folderConsolidation: FragmentType<
    typeof FolderBusinessEntity_FolderBusinessEntityConsolidation_Fragment
  >;
  readonly formVariant: FormVariant;
  readonly initialValues?: FragmentType<typeof MissionForm_Mission_Fragment>;
  readonly kind?: MissionKindEnum;
  readonly missionId?: Id;
  readonly onCancel?: () => void;
  readonly onSuccess?: (result: MissionMutationResult) => void;
  readonly setMustBlockAction?: (mustBlock: boolean) => void;
  readonly showCancelButton?: boolean;
  readonly stepIdsSubset?: readonly MissionFormTab[];
  readonly title?: string;
}

export const MissionForm = ({
  askMemoBeforeSubmit = true,
  folderConsolidation,
  formVariant,
  initialValues: ppInitialValues,
  kind: pKind = MissionKindEnum.Standard,
  missionId,
  onCancel: handleCancel,
  onSuccess: handleSuccess,
  setMustBlockAction,
  showCancelButton = true,
  stepIdsSubset,
  title,
}: Readonly<Props>) => {
  invariant(
    !!(formVariant !== FormVariant.Modification || missionId),
    'You must provide a missionId when formVariant is Modification',
  );

  const pInitialValues = getFragmentData(
    MissionForm_Mission_Fragment,
    ppInitialValues,
  );

  const kind = pInitialValues?.kind ?? pKind;

  const { initialValues } = useInitialValuesFactory(pInitialValues, kind);

  const subMissionTurnLists = useSubMissionTurnLists(
    initialValues.compositeMissionId ?? null,
  );

  const { handleSubmit } = useMissionSubmit({
    formVariant,
    handleSuccess,
    missionId,
    subMissionTurnLists,
  });

  const { steps, wizardChildren } = useMissionSteps({
    folderConsolidation,
    formVariant,
    kind,
    stepIdsSubset,
  });

  return (
    <Wizard
      askMemoBeforeSubmit={askMemoBeforeSubmit}
      formTitle={title}
      formVariant={formVariant}
      initialValues={initialValues}
      onCancel={handleCancel}
      onSubmit={handleSubmit}
      setMustBlockAction={setMustBlockAction}
      showCancelButton={showCancelButton}
      steps={steps}
    >
      {wizardChildren}
    </Wizard>
  );
};
